import * as Configuration from '../../app.constants.js';
import { LOGIN_OPTION_TYPE_SSO, LOGIN_OPTION_TYPE_ALCMEON } from './login.service';

const ROUTE_OPTIONS = {
  location: false, // do not update URL in browser top bar, so that people don't start bookmarking /alcmeon-login 
};

/**
 * Controller that handles the /<company-url-name>/login state
 * 
 * This controller routes to another state or redirect to an external url, as follows:
 * 
 * If the user just selected a login option on the login-options page, then route/redirect to it.
 * If the user already selected a login option in the past, then route/redirect to it.
 * If there is no login option configured for the company, or only an alcmeon-login option is configured, then route to the /alcmeon-login state
 * If there is only one SSO option configured for the company, then redirect to the SSO
 * Otherwise, redirect to the /login-options page, for the user to choose a login option
 * 
 * The user-selected login option is stored in the localstorage, but only after a successful login (either Alcmeon or SSO)
 * TODO implement showDisabled
 */
PreLoginController.$inject = ['company', 'LoginFactory', '$timeout', '$translate', '$state', '$stateParams',];
export function PreLoginController(company, LoginFactory, $timeout, $translate, $state, $stateParams) {

  init();

  function routeToSSO(loginOption) {
    let finalUrl = null;
    if ($stateParams.destState !== null) {
      const qsArgs = '?' + new URLSearchParams($stateParams.destState.qsArguments).toString();
      finalUrl = Configuration.constants.frontendBaseUrl + qsArgs + $state.href($stateParams.destState.name, $stateParams.destState.params);
    } else if ($stateParams.destUrl) {
      finalUrl = $stateParams.destUrl;
    } else {
      finalUrl = Configuration.constants.frontendBaseUrl + '#/' + company.id + '/inbox';
    }
    LoginFactory.initiateSSO(company.id, loginOption.saml_configuration_id, finalUrl);
  }

  function routeToLoginInAlcmeon() {
    $state.go('no-auth.company.login-in-alcmeon', LoginFactory.stateParamsToPropagate(), ROUTE_OPTIONS);
  }

  function routeToBackoffice() {
    let afterLoginUrl = $stateParams.destUrl; // see comment in SP-4987
    if (!afterLoginUrl) {
      afterLoginUrl = `${Configuration.constants.frontendBaseUrl}#/${company.id}/inbox`;
    }
    const params = `?companyId=${company.id}&afterLoginUrl=${encodeURIComponent(afterLoginUrl)}`;
    window.location.href = `${Configuration.constants.backofficeBaseUrl}/routes/login-from-alcmeon${params}`;
  }

  function routeToLoginOption(loginOption) {
    LoginFactory.saveTentativeLoginOption(company.id, loginOption);
    if (LoginFactory.isLoggedUserInternal() && Configuration.constants.isBackofficeActivated) {
      routeToBackoffice();
    } else if (loginOption.type === LOGIN_OPTION_TYPE_SSO) {
      routeToSSO(loginOption);
    } else {
      routeToLoginInAlcmeon();
    }
  }

  function routeToOptionSelection(loginOptions) {
    $state.go(
      'no-auth.company.login-option-selection',
      {
        ...LoginFactory.stateParamsToPropagate(),
        loginOptions
      },
      ROUTE_OPTIONS);
  }


  function init() {
    let availableOptions = company.login_options;
    if ($stateParams.forceSelection) {
      LoginFactory.clearRememberMyChoice(company.id);
    }
    // Remove options for which saml_configuration.is_active is 0 in the DB, unless explicitely told not to in the URL params
    if (!$stateParams.showInactive) {
      availableOptions = availableOptions.filter(option => option.type === LOGIN_OPTION_TYPE_ALCMEON || option.is_active);
    }
    // Use Alcmeon login for companies that don't have any login options defined in the DB (most of them don't)
    if (availableOptions.length === 0) {
      availableOptions.push({ type: LOGIN_OPTION_TYPE_ALCMEON });
    }
    // Add label to Alcmeon login option if not set in database
    availableOptions
      .filter(option => option.type === LOGIN_OPTION_TYPE_ALCMEON && !option.label)
      .forEach(option => option.label = $translate.instant('login/option-selection/in-alcmeon'));

    // decide whether we need to route to login-option-selection, or directly to either Alcmeon or an external SAML login page
    let justSelectedOrRememberedOrOnlyOption = $stateParams.selectedLoginOption || LoginFactory.rememberedLoginOption(company.id);
    if (!justSelectedOrRememberedOrOnlyOption && availableOptions.length === 1) {
      justSelectedOrRememberedOrOnlyOption = availableOptions[0];
    }
    if (justSelectedOrRememberedOrOnlyOption) {
      // we use a timeout to avoid ui-router re-rerouting to /login in some cases
      // (on logout for example, ui-router would get confused by a $locationChangeSuccess event 
      //  that would arrive when it has already routed to /login-in-alcmeon)
      $timeout(() => routeToLoginOption(justSelectedOrRememberedOrOnlyOption));
    } else {
      $timeout(() => routeToOptionSelection(availableOptions));
    }
  }

}
